import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, Subscription, defer, of, throwError } from 'rxjs';
import {
  catchError,
  finalize,
  map,
  shareReplay,
  switchMap,
} from 'rxjs/operators';
import { ApiHttpService } from '../api-http.service';
import { ApiEndpointsService } from '../api-endpoints.service';
import { AuthService } from './auth.service';

interface IAceExchangePayload {
  accessToken: string;
  refreshToken?: string;
  tokenType?: string;
  expiresInSeconds?: number;
  user?: { id: string; email: string; roles: string[]; tenantId: string };
}

interface IAceExchangeResponse extends IAceExchangePayload {
  data?: IAceExchangePayload;
}

@Injectable({
  providedIn: 'root',
})
export class AceAuthService implements OnDestroy {
  private static readonly EXPIRY_SKEW_MS = 30_000;

  private token: string | null = null;
  private aceRefreshToken: string | null = null;
  private activeTenantId: string | null = null;
  private expiresAt = 0;
  private inFlight$: Observable<string> | null = null;
  private inFlightContextVersion: number | null = null;
  private contextVersion = 0;
  private authSessionVersion = 0;
  private readonly contextChangesSubject = new Subject<void>();
  private readonly authSessionSubscription: Subscription;

  readonly contextChanges$ = this.contextChangesSubject.asObservable();

  constructor(
    private apiHttpService: ApiHttpService,
    private apiEndpointsService: ApiEndpointsService,
    private authService: AuthService
  ) {
    this.authSessionVersion = this.authService.getSessionVersion();
    this.authSessionSubscription = this.authService.sessionChanges$.subscribe(
      () => {
        this.authSessionVersion = this.authService.getSessionVersion();
        this.invalidateContext(true);
      }
    );
  }

  setTenantId(tenantId: string | null): void {
    const normalizedTenantId = tenantId?.trim() || null;
    if (this.activeTenantId !== normalizedTenantId) {
      this.activeTenantId = normalizedTenantId;
      this.invalidateContext();
    }
  }

  getToken(tenantId?: string): Observable<string> {
    this.syncAuthSession();
    if (tenantId) {
      this.setTenantId(tenantId);
    }
    const effectiveTenantId = this.getEffectiveTenantId();
    if (effectiveTenantId !== this.activeTenantId) {
      this.setTenantId(effectiveTenantId);
    }
    if (this.token && Date.now() < this.expiresAt) {
      return of(this.token);
    }
    if (!this.inFlight$) {
      const contextVersion = this.contextVersion;
      this.inFlightContextVersion = contextVersion;
      this.inFlight$ = defer(() => {
        if (this.aceRefreshToken) {
          return this.refreshAceToken(contextVersion).pipe(
            catchError(error => {
              if (!this.isInvalidAceRefreshError(error)) {
                return throwError(() => error);
              }
              this.aceRefreshToken = null;
              return this.exchange(this.activeTenantId, contextVersion);
            })
          );
        }
        return this.exchange(this.activeTenantId, contextVersion);
      }).pipe(
        finalize(() => {
          if (this.inFlightContextVersion === contextVersion) {
            this.inFlight$ = null;
            this.inFlightContextVersion = null;
          }
        }),
        shareReplay(1)
      );
    }
    return this.inFlight$;
  }

  getContextKey(): string {
    this.syncAuthSession();
    const effectiveTenantId = this.getEffectiveTenantId();
    if (effectiveTenantId !== this.activeTenantId) {
      this.setTenantId(effectiveTenantId);
    }
    return [
      this.authSessionVersion,
      this.contextVersion,
      this.activeTenantId ?? 'no-tenant',
    ].join(':');
  }

  getUserId(): string | null {
    return this.userId;
  }

  clear(): void {
    this.token = null;
    this.expiresAt = 0;
  }

  ngOnDestroy(): void {
    this.authSessionSubscription.unsubscribe();
    this.contextChangesSubject.complete();
  }

  private userId: string | null = null;

  private getEffectiveTenantId(): string | null {
    let candidate: string | null = null;
    let hasRouteTenantContext = false;
    try {
      if (typeof window !== 'undefined' && window.location?.pathname) {
        const path = window.location.pathname;
        const gpMatch = path.match(/\/gp\/([^/]+)/);
        const vehicleMatch = path.match(/\/vehicle\/([^/]+)/);
        hasRouteTenantContext = !!(gpMatch || vehicleMatch);

        // The selected vehicle is the most precise context. A persisted GP
        // tenant can be stale after switching accounts or funds, so prefer the
        // vehicle mapping whenever the URL contains one.
        if (
          vehicleMatch &&
          vehicleMatch[1] &&
          typeof localStorage !== 'undefined'
        ) {
          const vehicleId = vehicleMatch[1];
          candidate = localStorage.getItem(`vehicle_tenant_map_${vehicleId}`);
        }

        if (!candidate && gpMatch && gpMatch[1]) {
          const gpId = gpMatch[1];
          if (gpId.startsWith('019')) {
            // Preserve direct tenant IDs used by legacy GP deep links.
            candidate = gpId;
          } else if (typeof localStorage !== 'undefined') {
            candidate = localStorage.getItem(`gp_tenant_map_${gpId}`);
          }
        }
      }

      // A route identifies the selected fund context. If its mapping is not
      // available yet, do not silently reuse the previous tenant or a global
      // persisted selection while the new route is being initialized.
      if (!candidate && !hasRouteTenantContext) {
        candidate = this.activeTenantId;
      }

      if (
        !candidate &&
        !hasRouteTenantContext &&
        typeof localStorage !== 'undefined'
      ) {
        candidate =
          localStorage.getItem('selectedTenantId') ||
          localStorage.getItem('activeTenantId');
      }

      if (candidate && typeof localStorage !== 'undefined') {
        const mapped =
          localStorage.getItem(`gp_tenant_map_${candidate}`) ||
          localStorage.getItem(`vehicle_tenant_map_${candidate}`);
        if (mapped) {
          candidate = mapped;
        }
      }
    } catch {
      // Ignore in non-browser environment
    }

    return candidate?.trim() || null;
  }

  private exchange(
    tenantId: string | null,
    contextVersion: number
  ): Observable<string> {
    return defer(() => {
      return this.authService
        .getFreshBearerToken()
        .pipe(
          map(bearerToken => ({ headers: { Authorization: bearerToken } }))
        );
    }).pipe(
      switchMap(options =>
        this.exchangeRequest(tenantId, contextVersion, options).pipe(
          catchError(error =>
            this.retryExchangeAfterKeycloakRefresh(
              error,
              tenantId,
              contextVersion
            )
          )
        )
      ),
      map((res: unknown) => {
        if (this.contextVersion !== contextVersion) {
          throw new Error('Tenant context changed during token exchange');
        }
        return this.applyTokenResponse(res, contextVersion, false);
      })
    );
  }

  private refreshAceToken(contextVersion: number): Observable<string> {
    const refreshToken = this.aceRefreshToken;
    if (!refreshToken) {
      return throwError(() => new Error('ACE refresh token not available'));
    }

    return this.apiHttpService
      .post(
        this.apiEndpointsService.aceRefresh(),
        { refreshToken },
        { headers: { 'Content-Type': 'application/json' } }
      )
      .pipe(
        map(response => this.applyTokenResponse(response, contextVersion, true))
      );
  }

  private applyTokenResponse(
    res: unknown,
    contextVersion: number,
    requireRefreshToken: boolean
  ): string {
    if (this.contextVersion !== contextVersion) {
      throw new Error('Tenant context changed during token exchange');
    }

    const response = res as IAceExchangeResponse;
    const data = response?.data ?? response;
    if (!data?.accessToken) {
      throw new Error('Spike token exchange returned no access token');
    }

    const expiresInSeconds = Number(data.expiresInSeconds);
    if (!Number.isFinite(expiresInSeconds) || expiresInSeconds <= 0) {
      throw new Error('Spike token exchange returned an invalid expiry');
    }

    if (requireRefreshToken && !data.refreshToken) {
      throw new Error('ACE refresh response returned no refresh token');
    }

    this.userId = data.user?.id ?? this.userId;
    this.token = data.accessToken;
    if (typeof data.refreshToken === 'string' && data.refreshToken.length > 0) {
      this.aceRefreshToken = data.refreshToken;
    }
    this.expiresAt =
      Date.now() + expiresInSeconds * 1000 - AceAuthService.EXPIRY_SKEW_MS;
    return data.accessToken;
  }

  private exchangeRequest(
    tenantId: string | null,
    contextVersion: number,
    options: { headers: { Authorization: string } }
  ): Observable<unknown> {
    const body = tenantId ? { tenantId } : {};
    return this.apiHttpService.post(
      this.apiEndpointsService.aceExchange(),
      body,
      options
    );
  }

  private retryExchangeAfterKeycloakRefresh(
    error: unknown,
    tenantId: string | null,
    contextVersion: number
  ): Observable<unknown> {
    if (
      this.contextVersion !== contextVersion ||
      !(error instanceof HttpErrorResponse) ||
      error.status !== 401
    ) {
      return throwError(() => error);
    }

    return this.authService.forceRefreshBearerToken().pipe(
      catchError(refreshError => {
        if (
          this.contextVersion === contextVersion &&
          this.isInvalidRefreshCredential(refreshError)
        ) {
          this.clear();
          this.authService.logout();
        }
        return throwError(() => refreshError);
      }),
      switchMap(bearerToken =>
        this.exchangeRequest(tenantId, contextVersion, {
          headers: { Authorization: bearerToken },
        }).pipe(
          catchError(retryError => {
            if (
              this.contextVersion === contextVersion &&
              retryError instanceof HttpErrorResponse &&
              retryError.status === 401
            ) {
              this.clear();
              this.authService.logout();
            }
            return throwError(() => retryError);
          })
        )
      )
    );
  }

  private isInvalidRefreshCredential(error: unknown): boolean {
    if (error instanceof HttpErrorResponse) {
      return error.status === 400 || error.status === 401;
    }
    return (
      error instanceof Error && error.message === 'Refresh token not available'
    );
  }

  private isInvalidAceRefreshError(error: unknown): boolean {
    return (
      error instanceof HttpErrorResponse &&
      (error.status === 400 || error.status === 401)
    );
  }

  private getAuthSessionVersion(): number {
    return this.authService.getSessionVersion();
  }

  private syncAuthSession(): void {
    const currentVersion = this.getAuthSessionVersion();
    if (currentVersion !== this.authSessionVersion) {
      this.authSessionVersion = currentVersion;
      this.invalidateContext(true);
    }
  }

  private invalidateContext(resetTenant = false): void {
    this.contextVersion += 1;
    if (resetTenant) {
      this.activeTenantId = null;
      this.clearPersistedTenantContext();
    }
    this.clearSession();
    this.inFlight$ = null;
    this.inFlightContextVersion = null;
    this.contextChangesSubject.next();
  }

  private clearSession(): void {
    this.clear();
    this.aceRefreshToken = null;
    this.userId = null;
  }

  private clearPersistedTenantContext(): void {
    try {
      if (typeof localStorage === 'undefined') {
        return;
      }
      const keysToRemove: string[] = [];
      for (let index = 0; index < localStorage.length; index += 1) {
        const key = localStorage.key(index);
        if (
          key === 'selectedTenantId' ||
          key === 'activeTenantId' ||
          key?.startsWith('gp_tenant_map_') ||
          key?.startsWith('vehicle_tenant_map_')
        ) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch {
      return;
    }
  }
}
